import type { paths } from "@wirevod/contracts";
import createClient, { type Middleware } from "openapi-fetch";
import { notifyApiResponse, webHeaders } from "../config";
import { refreshSession } from "./refresh";
import { getSession } from "./session";

let onSessionLost: () => void = () => undefined;

/** Called when a request is unauthorised and the session can't be refreshed. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

const NO_RETRY = new Set([
  "/v1/auth/login",
  "/v1/auth/signup",
  "/v1/auth/google",
  "/v1/auth/refresh",
]);
const originals = new WeakMap<Request, Request>();

const auth: Middleware = {
  onRequest({ request }) {
    for (const [name, value] of Object.entries(webHeaders())) request.headers.set(name, value);
    const session = getSession();
    if (session) request.headers.set("authorization", `Bearer ${session.accessToken}`);
    // Keep an unread copy so the request can be replayed after a refresh.
    originals.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    await notifyApiResponse(response);
    if (response.status !== 401) return response;
    const path = new URL(request.url).pathname;
    if (NO_RETRY.has(path)) return response;
    const replay = originals.get(request);
    if (!(await refreshSession()) || !replay) {
      onSessionLost();
      return response;
    }
    const session = getSession();
    if (session) replay.headers.set("authorization", `Bearer ${session.accessToken}`);
    const retried = await globalThis.fetch(replay);
    if (retried.status === 401) onSessionLost();
    return retried;
  },
};

/** Typed client for the WireVoD API (same origin). */
export const api = createClient<paths>({
  // Absolute same-origin base: `Request` needs it outside browsers (tests).
  baseUrl: globalThis.location?.origin ?? "",
  credentials: "same-origin",
  // Resolved per call, so instrumentation and test doubles of fetch apply.
  fetch: (request) => globalThis.fetch(request),
});
api.use(auth);

export type ApiError = { error: string; code: string; message: string; details?: unknown };

/** Throws the API error body so TanStack Query surfaces it. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    throw new ApiRequestError(result.response.status, result.error as ApiError | undefined);
  }
  return result.data;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError | undefined,
  ) {
    super(body?.message ?? `Request failed (${status})`);
    this.name = "ApiRequestError";
  }

  get code(): string | undefined {
    return this.body?.code;
  }
}
